import { FileCsvIcon, FilePdfIcon, XIcon } from "@phosphor-icons/react";
import type { ComponentPropsWithoutRef, ReactNode } from "react";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import imageAttachmentPlaceholderUrl from "@/assets/images/image-attachment-placeholder.png?url";
import {
  Tooltip,
  TooltipContent,
  TooltipPortal,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/data-display/tooltip";
import { IconButton } from "@/components/ui/icon-button";
import { cn } from "@/lib/utils";

/** Thread message row vs composer attachment strip (Figma: gray-300 border + remove). */
export type AttachmentFileRowVariant = "thread" | "input";

const iconTileClassName =
  "flex size-6 shrink-0 items-center justify-center rounded bg-violet-50";

const fileNameClassName =
  "block min-w-0 w-full overflow-hidden text-left text-xs text-ellipsis whitespace-nowrap text-gray-900";

interface AttachmentBubbleFileRowProps extends ComponentPropsWithoutRef<"div"> {
  filename: string;
  icon: ReactNode;
  onRemove?: () => void;
  variant?: AttachmentFileRowVariant;
}

const AttachmentBubbleFileRow = ({
  filename,
  icon,
  className,
  onRemove,
  variant = "thread",
  ...rest
}: AttachmentBubbleFileRowProps) => {
  const isInput = variant === "input";
  const filenameRef = useRef<HTMLSpanElement>(null);
  const [isFilenameTruncated, setIsFilenameTruncated] = useState(false);

  const updateFilenameTruncation = useCallback(() => {
    const el = filenameRef.current;
    if (!el) return;
    setIsFilenameTruncated(el.scrollWidth > el.clientWidth);
  }, []);

  useLayoutEffect(() => {
    updateFilenameTruncation();
    const el = filenameRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(() => {
      requestAnimationFrame(updateFilenameTruncation);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [filename, updateFilenameTruncation]);

  const filenameSpan = (
    <span ref={filenameRef} className={fileNameClassName}>
      {filename}
    </span>
  );

  const filenameBlock =
    isFilenameTruncated && filename.length > 0 ? (
      <TooltipProvider delayDuration={200}>
        <Tooltip>
          <TooltipTrigger asChild>{filenameSpan}</TooltipTrigger>
          <TooltipPortal>
            <TooltipContent
              side="top"
              sideOffset={15}
              className={cn(
                "max-w-[min(100vw-2rem,320px)] border-0 bg-gray-900 font-normal text-white",
                "whitespace-normal wrap-break-word text-left",
              )}
              align="start"
            >
              {filename}
            </TooltipContent>
          </TooltipPortal>
        </Tooltip>
      </TooltipProvider>
    ) : (
      filenameSpan
    );

  return (
    <div
      className={cn(
        "flex h-10 min-w-0 items-center overflow-hidden rounded-lg border bg-white shadow-xs",
        isInput
          ? cn(
              "max-w-[min(100%,180px)] cursor-pointer gap-1 border-gray-300 pl-[7px] pr-1",
              "transition-colors hover:bg-gray-50",
              "has-[button:hover]:bg-white has-[button[data-hovered]]:bg-white",
            )
          : "max-w-[240px] gap-2 border-gray-200 pl-[7px] pr-3",
        className,
      )}
      {...rest}
    >
      <div className={iconTileClassName}>{icon}</div>
      <div className="min-w-0 flex-1 overflow-hidden">{filenameBlock}</div>
      {onRemove ? (
        <IconButton
          icon={<XIcon weight="regular" />}
          variant="ghost"
          size="sm"
          aria-label={`Remove ${filename}`}
          onPress={onRemove}
          className="ml-2.5 shrink-0 rounded-full"
          tooltipLabel="Remove file"
          tooltipPosition="right"
          offset={10}
          crossOffset={20}
        />
      ) : null}
    </div>
  );
};

export interface AttachmentBubblePdfProps
  extends ComponentPropsWithoutRef<"div"> {
  filename: string;
  onRemove?: () => void;
  variant?: AttachmentFileRowVariant;
}

export const AttachmentBubblePdf = ({
  filename,
  className,
  onRemove,
  variant = "thread",
  ...rest
}: AttachmentBubblePdfProps) => (
  <AttachmentBubbleFileRow
    filename={filename}
    className={className}
    icon={
      <FilePdfIcon
        size={16}
        weight="duotone"
        className="shrink-0 text-violet-600"
        aria-hidden
      />
    }
    onRemove={onRemove}
    variant={variant}
    {...rest}
  />
);

export interface AttachmentBubbleCsvProps
  extends ComponentPropsWithoutRef<"div"> {
  filename: string;
  onRemove?: () => void;
  variant?: AttachmentFileRowVariant;
}

export const AttachmentBubbleCsv = ({
  filename,
  className,
  onRemove,
  variant = "thread",
  ...rest
}: AttachmentBubbleCsvProps) => (
  <AttachmentBubbleFileRow
    filename={filename}
    className={className}
    icon={
      <FileCsvIcon
        size={16}
        weight="duotone"
        className="shrink-0 text-violet-600"
        aria-hidden
      />
    }
    onRemove={onRemove}
    variant={variant}
    {...rest}
  />
);

export interface AttachmentBubbleImageProps
  extends Omit<ComponentPropsWithoutRef<"img">, "width" | "height" | "src"> {
  src?: string;
}

export const AttachmentBubbleImage = ({
  src,
  alt = "",
  className,
  onError,
  ...rest
}: AttachmentBubbleImageProps) => {
  const primarySrc = src?.trim();
  const [loadFailed, setLoadFailed] = useState(false);

  useEffect(() => {
    setLoadFailed(false);
  }, [src]);

  const displaySrc =
    !primarySrc || loadFailed ? imageAttachmentPlaceholderUrl : primarySrc;

  return (
    <img
      src={displaySrc}
      alt={alt}
      onError={(event) => {
        if (displaySrc !== imageAttachmentPlaceholderUrl) {
          setLoadFailed(true);
        }
        onError?.(event);
      }}
      className={cn(
        "size-[50px] shrink-0 rounded-[5px] object-cover",
        className,
      )}
      {...rest}
    />
  );
};

export interface AttachmentBubbleRowItem {
  kind: "image" | "csv" | "pdf";
  name: string;
  url?: string;
}

/** The attachments sent with a chat message, right-aligned above its bubble. */
export const AttachmentBubbleRow = ({
  attachments,
}: {
  attachments: AttachmentBubbleRowItem[];
}) =>
  attachments.length > 0 ? (
    <div className="flex w-full min-w-0 flex-wrap justify-end gap-1.5">
      {attachments.map(({ kind, name, url }, index) => (
        <div className="shrink-0" key={url ?? `${kind}:${name}:${index}`}>
          {kind === "image" ? (
            <AttachmentBubbleImage src={url} alt={name} />
          ) : kind === "csv" ? (
            <AttachmentBubbleCsv filename={name} />
          ) : (
            <AttachmentBubblePdf filename={name} />
          )}
        </div>
      ))}
    </div>
  ) : null;
